import { useState } from 'react';

/**
 * Custom hook for managing localStorage with React state
 * @param {string} key - The localStorage key
 * @param {any} initialValue - The initial value if key doesn't exist
 * @returns {[any, function]} - [storedValue, setValue]
 */
export const useLocalStorage = (key, initialValue) => {
  // Get from local storage then parse stored json or return initialValue
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.warn(`Error reading localStorage key "${key}":`, error);
      return initialValue;
    }
  });

  // Return a wrapped version of useState's setter function that persists the new value to localStorage
  const setValue = (value) => {
    try {
      // Allow value to be a function so we have the same API as useState
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      window.localStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      console.warn(`Error setting localStorage key "${key}":`, error);
    }
  };

  return [storedValue, setValue];
};

/**
 * Hook for managing investment scenarios
 */
export const useInvestmentScenarios = () => {
  const [scenarios, setScenarios] = useLocalStorage('investment-scenarios', []);
  const [activeScenarioId, setActiveScenarioId] = useLocalStorage('active-scenario-id', null);

  const saveScenario = (scenarioData, name) => {
    const newScenario = {
      id: Date.now().toString(),
      name: name || `Scenario ${scenarios.length + 1}`,
      data: scenarioData,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    setScenarios(prev => [...prev, newScenario]);
    return newScenario.id;
  };

  const updateScenario = (id, updates) => {
    setScenarios(prev => prev.map(scenario => 
      scenario.id === id 
        ? { ...scenario, ...updates, updatedAt: new Date().toISOString() }
        : scenario
    ));
  };

  const deleteScenario = (id) => {
    setScenarios(prev => prev.filter(scenario => scenario.id !== id));
    if (activeScenarioId === id) {
      setActiveScenarioId(null);
    }
  };

  const getActiveScenario = () => {
    return scenarios.find(scenario => scenario.id === activeScenarioId);
  };

  return {
    scenarios,
    activeScenarioId,
    setActiveScenarioId,
    saveScenario,
    updateScenario,
    deleteScenario,
    getActiveScenario
  };
};

/**
 * Hook for managing user preferences
 */
export const useUserPreferences = () => {
  const [preferences, setPreferences] = useLocalStorage('user-preferences', {
    defaultInitialInvestment: 250000,
    defaultTargetNetValue: 2000000,
    defaultStartDate: new Date(2025, 0, 1).toISOString(),
    defaultEndDate: new Date(2029, 11, 28).toISOString(),
    yearFilter: 'all',
    showFilter: 'all',
    autoSave: true,
    notifications: true
  });

  const updatePreference = (key, value) => {
    setPreferences(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const resetPreferences = () => {
    setPreferences({
      defaultInitialInvestment: 250000,
      defaultTargetNetValue: 2000000,
      defaultStartDate: new Date(2025, 0, 1).toISOString(),
      defaultEndDate: new Date(2029, 11, 28).toISOString(),
      yearFilter: 'all',
      showFilter: 'all',
      autoSave: true,
      notifications: true
    });
  };

  return {
    preferences,
    updatePreference,
    resetPreferences
  };
};
